import { useEffect, useRef, useState } from "react";
import Browser from "../browser";
import BoundaryRect from "../models/BoundaryRect";
import ResizeGrab from "./ResizeGrab";
import { useRedraw } from "../models/useRerender";
import WatchableValue from "../models/WatchableValue";
import { useOnChangeWatcher } from "../models/useOnChangeWatcher";
import type Room from "../models/Room";
import { MoveHandler } from "../modelViews/MoveHandler";
import Point from "../models/Point";
import type Page from "../models/Page";

export default function RoomBoundaryView(props: {
  room: Room;
  page: Page;
  currentZoom: WatchableValue<number>;
  selected: boolean;
  onChange: (newBoundaryRect: BoundaryRect) => void;
  onClick?: () => void;
  onDoubleClick?: () => void;
  onRedraw?: () => void;
}) {
  const { selected, onChange, onClick, onDoubleClick, onRedraw, currentZoom, room, page } = props;
  const boundaryRect = useRef<BoundaryRect>(room.boundingRectInFloorSpace.copy());
  const diagnosticMode = state.settings.diagnosticMode;
  const xmoveHandler = useRef<MoveHandler | undefined>(undefined);
  const initialPoint = useRef<Point | undefined>(undefined);
  const delta = useRef<Point | undefined>(undefined);
  const moveStarted = useRef(false);
  const mainPage = room.parentFloor?.getOrCreatePage() || page
  const [hover, setHover] = useState(false);

  useOnChangeWatcher(room, (changedProperty) => {
    boundaryRect.current = room.boundingRectInFloorSpace;
    return true;
  });
  useOnChangeWatcher(mainPage);
  const redrawTimer = useRef<any>(null);

  const redraw = useRedraw();
  const thisElement = useRef<HTMLDivElement>(null);
  const moveAreaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!diagnosticMode) {
      xmoveHandler.current = initMoveHander();
      return () => {
        xmoveHandler.current?.release();
      };
    }
  });

  useOnChangeWatcher(mainPage?.currentZoom, (changedProperty, changedObject) => {
    return true;
  });

  function initMoveHander(): MoveHandler | undefined {
    console.log("initMoveHander for RoomBoundaryView " + room.fullTag);

    var xmoveHandler = new MoveHandler(thisElement, "#moveArea", undefined, mainPage); // no itx

    xmoveHandler.onClick = () => {
      onClick && onClick();
    };

    xmoveHandler.onDblClick = () => {
      onDoubleClick && onDoubleClick();
    };

    xmoveHandler.onMouseDown = (pLocal: Point) => {
      moveStarted.current = false;
      if (room.roomtoFloorDrawingTransform) {
        delta.current = new Point(
          boundaryRect.current.min.x - room.roomtoFloorDrawingTransform.position.x,
          boundaryRect.current.min.y - room.roomtoFloorDrawingTransform.position.y,
        );
      }
      initialPoint.current = pLocal;
    };

    xmoveHandler.onCancel = () => {
      if (initialPoint.current && room.roomtoFloorDrawingTransform) {
        room.roomtoFloorDrawingTransform.position = initialPoint.current.clone();

        redraw();
      }
    };

    xmoveHandler.onMouseUp = () => { };

    xmoveHandler.onMoveStarted = () => {
      moveStarted.current = true;
    };

    xmoveHandler.onMoveInfo = () => { };

    xmoveHandler.onMoveFinished = (pLocal: Point, pFloor: Point) => {
      if (moveStarted.current && room.roomtoFloorDrawingTransform) {
        const dx = boundaryRect.current.min.x - room.roomtoFloorDrawingTransform.position.x;
        const dy = boundaryRect.current.min.y - room.roomtoFloorDrawingTransform.position.y;

        const w = boundaryRect.current.width;
        const h = boundaryRect.current.height;

        boundaryRect.current.min.x = pLocal.x;
        boundaryRect.current.min.y = pLocal.y;

        boundaryRect.current.max.x = pLocal.x + w;
        boundaryRect.current.max.y = pLocal.y + h;

        onChange(boundaryRect.current);

        boundaryRect.current = room.boundingRectInFloorSpace.copy();

        if (onRedraw) {
          onRedraw();
        }
      }
      moveStarted.current = false;
    };

    return xmoveHandler;
  }

  function setSize(side: "top" | "bottom" | "left" | "right", sx: number, sy: number) {
    // no itx
    if (thisElement.current) {
      switch (side) {
        case "bottom": {
          boundaryRect.current.max.y = boundaryRect.current.min.y + sy;
          break;
        }
        case "top": {
          // no itx
          boundaryRect.current.min.y = boundaryRect.current.max.y - sy;
          break;
        }

        case "left": {
          // no itx
          boundaryRect.current.min.x = boundaryRect.current.max.x - sx;
          break;
        }

        case "right": {
          boundaryRect.current.max.x = boundaryRect.current.min.x + sx;
          break;
        }
      }
    }
    redraw();
  }

  function setSizeEnd() {
    onChange(boundaryRect.current);
    boundaryRect.current = room.boundingRectInFloorSpace.copy();
    redraw();
  }

  if (!boundaryRect.current) {
    return null;
  }

  const boundaryLeft = boundaryRect.current.min.x;
  const boundaryTop = boundaryRect.current.min.y;
  const boundaryWidth = boundaryRect.current.width;
  const boundaryHeight = boundaryRect.current.height;

  const styleRoomBoundaryRect: any = {
    position: "absolute",
    left: `${boundaryLeft}px`,
    top: `${boundaryTop}px`,
    width: `${boundaryWidth}px`,
    height: `${boundaryHeight}px`,
  };

  const initialSize = {
    sx: boundaryRect.current.width,
    sy: boundaryRect.current.height,
  };

  const touchSelected = selected && Browser.isTouch() ? "touchSelected" : ""; // no itx

  const showFull = mainPage?.editRooms || room.selected

  if (!showFull) {
    return <div
      id="boundaryRect" style={{ ...styleRoomBoundaryRect, position: "absolute", border: "1px solid #888" }}
    />
  }

  return (
    <div
      id="boundaryRect"
      ref={thisElement}
      className={`boundaryRect  ${touchSelected}  ${selected ? "selected" : ""} `}
      style={styleRoomBoundaryRect}
    >
      <div id="moveArea" className="moveArea" ref={moveAreaRef} />

      <div className="boundaryOutline" style={{}} />

      <ResizeGrab
        key="BLeft"
        side="left" // no itx
        currentZoom={currentZoom}
        initialSize={initialSize}
        setSize={setSize}
        setSizeEnd={setSizeEnd}
        selected={selected}
      />

      <ResizeGrab
        key="BRight"
        side="right"
        currentZoom={currentZoom}
        initialSize={initialSize}
        setSize={setSize}
        setSizeEnd={setSizeEnd}
        selected={selected}
      />

      <ResizeGrab
        key="BTop"
        side="top" // no itx
        currentZoom={currentZoom}
        initialSize={initialSize}
        setSize={setSize}
        setSizeEnd={setSizeEnd}
        selected={selected}
      />

      <ResizeGrab
        key="BBottom"
        side="bottom"
        currentZoom={currentZoom}
        initialSize={initialSize}
        setSize={setSize}
        setSizeEnd={setSizeEnd}
        selected={selected}
      />
    </div>
  );
}
